/**
 * Service Accounts (ADR-0052) — provisioned integration IDENTITIES (not humans).
 *
 * An operator provisions a service account with its OWN permissions + app scope,
 * then mints rotatable API keys for it. `enabled` is the kill-switch; the app scope
 * is mandatory-by-default ("all apps" is an explicit, warned choice). Personal tokens
 * live elsewhere (Profilo → API key); this page is integrations, not humans.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Check, Copy, KeyRound, Pencil, Plus, Trash2, XCircle } from "lucide-react";
import * as React from "react";
import { useTranslation } from "react-i18next";

import { PermissionPicker } from "@/components/auth/PermissionPicker";
import { Alert } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { EmptyState, PageContainer, PageContent, PageHeader } from "@/components/ui/page";
import { Switch } from "@/components/ui/switch";
import { Table, Tbody, Td, Th, Thead, Tr } from "@/components/ui/table";
import { appsApi } from "@/lib/api-apps";
import {
  serviceAccountsApi,
  type ServiceAccount,
  type ServiceAccountKeyCreated,
  type ServiceAccountScope,
} from "@/lib/api-service-accounts";
import { usePermissions } from "@/lib/permissions";

const SELECT = "h-9 w-full rounded-md border border-border bg-surface px-2 text-sm";

export function ServiceAccountsPage() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const { can } = usePermissions();
  const canWrite = can("service_accounts", "write");
  const canDelete = can("service_accounts", "delete");

  const list = useQuery({ queryKey: ["service-accounts"], queryFn: () => serviceAccountsApi.list() });
  const apps = useQuery({ queryKey: ["apps", "list"], queryFn: () => appsApi.list() });
  // ADR-0053: a null app_id is a TRAY-target account (platform APIs), NOT "all apps".
  const appName = (id: string | null) =>
    id
      ? apps.data?.find((a) => a.id === id)?.name ?? id
      : t("serviceAccounts.targetTray", "Tray (piattaforma)");

  const [editing, setEditing] = React.useState<ServiceAccount | "new" | null>(null);
  const invalidate = () => qc.invalidateQueries({ queryKey: ["service-accounts"] });

  const remove = useMutation({
    mutationFn: (id: string) => serviceAccountsApi.remove(id),
    onSuccess: invalidate,
  });
  const toggleEnabled = useMutation({
    mutationFn: (sa: ServiceAccount) => serviceAccountsApi.update(sa.id, { enabled: !sa.enabled }),
    onSuccess: invalidate,
  });

  return (
    <PageContainer>
      <PageHeader
        title={t("serviceAccounts.title", "Service Account")}
        description={t(
          "serviceAccounts.subtitle",
          "Identità di servizio per le integrazioni — permessi propri, scope su un'app, chiavi ruotabili. Non sono utenti.",
        )}
        actions={
          canWrite ? (
            <Button onClick={() => setEditing("new")}>
              <Plus className="mr-1.5 h-4 w-4" strokeWidth={1.75} /> {t("serviceAccounts.new", "Nuovo")}
            </Button>
          ) : undefined
        }
      />
      <PageContent>
        {(list.data ?? []).length === 0 ? (
          <EmptyState
            icon={KeyRound}
            title={t("serviceAccounts.emptyTitle", "Nessun service account")}
            description={t(
              "serviceAccounts.emptyHint",
              "Crea un'identità di servizio per far accedere un'integrazione (es. REST) a una tua app.",
            )}
          />
        ) : (
          <Table>
            <Thead>
              <Tr>
                <Th>{t("common.name", "Nome")}</Th>
                <Th>{t("serviceAccounts.app", "App")}</Th>
                <Th>{t("serviceAccounts.permissions", "Permessi")}</Th>
                <Th>{t("common.status", "Stato")}</Th>
                <Th className="text-right">{t("common.actions", "Azioni")}</Th>
              </Tr>
            </Thead>
            <Tbody>
              {(list.data ?? []).map((sa) => (
                <Tr key={sa.id}>
                  <Td className="font-medium">{sa.name}</Td>
                  <Td className="text-muted-foreground">{appName(sa.app_id)}</Td>
                  <Td className="text-muted-foreground">{sa.permissions.length}</Td>
                  <Td>
                    <button
                      type="button"
                      disabled={!canWrite}
                      onClick={() => toggleEnabled.mutate(sa)}
                      title={t("serviceAccounts.toggleHint", "Abilita/disabilita (kill-switch)")}
                    >
                      <Badge tone={sa.enabled ? "active" : "danger"}>
                        {sa.enabled
                          ? t("common.enabled", "Abilitato")
                          : t("common.disabled", "Disabilitato")}
                      </Badge>
                    </button>
                  </Td>
                  <Td className="text-right">
                    <div className="flex justify-end gap-1">
                      <Button variant="ghost" size="icon" onClick={() => setEditing(sa)}>
                        <Pencil className="h-4 w-4" strokeWidth={1.5} />
                      </Button>
                      {canDelete && (
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => {
                            if (window.confirm(t("serviceAccounts.confirmDelete", `Eliminare "${sa.name}" e le sue chiavi?`)))
                              remove.mutate(sa.id);
                          }}
                        >
                          <Trash2 className="h-4 w-4 text-danger" strokeWidth={1.5} />
                        </Button>
                      )}
                    </div>
                  </Td>
                </Tr>
              ))}
            </Tbody>
          </Table>
        )}
      </PageContent>

      {editing && (
        <ServiceAccountDialog
          sa={editing === "new" ? null : editing}
          canWrite={canWrite}
          apps={apps.data ?? []}
          onClose={() => setEditing(null)}
          onSaved={invalidate}
        />
      )}
    </PageContainer>
  );
}

function ServiceAccountDialog({
  sa,
  canWrite,
  apps,
  onClose,
  onSaved,
}: {
  sa: ServiceAccount | null;
  canWrite: boolean;
  apps: { id: string; name: string }[];
  onClose: () => void;
  onSaved: () => void;
}) {
  const { t } = useTranslation();
  const [name, setName] = React.useState(sa?.name ?? "");
  const [description, setDescription] = React.useState(sa?.description ?? "");
  const [permissions, setPermissions] = React.useState<string[]>(sa?.permissions ?? []);
  // ADR-0053 TARGET: 'tray' (platform APIs, no app) | 'app' (confined to ONE app). The old
  // ambiguous "all apps" is gone. New accounts default to 'app' (the common integration case).
  const [scope, setScope] = React.useState<ServiceAccountScope>(sa?.scope ?? "app");
  const [appId, setAppId] = React.useState<string>(sa?.app_id ?? "");
  // ADR-0053 S6: an app SA acts AS an app ROLE (its /svc boundary) — NOT Tray permissions.
  const [roleId, setRoleId] = React.useState<string>(sa?.role_id ?? "");
  const [enabled, setEnabled] = React.useState(sa?.enabled ?? true);
  const [created, setCreated] = React.useState<ServiceAccountKeyCreated | null>(null);

  // Roles of the chosen app (only needed for an app-target account).
  const rolesQ = useQuery({
    queryKey: ["apps", appId, "roles"],
    queryFn: () => appsApi.listRoles(appId),
    enabled: scope === "app" && !!appId,
  });

  const save = useMutation({
    mutationFn: () => {
      const body =
        scope === "app"
          ? { name, description: description || null, scope, app_id: appId, role_id: roleId, permissions: [], enabled }
          : { name, description: description || null, scope, app_id: null, permissions, enabled };
      return sa ? serviceAccountsApi.update(sa.id, body) : serviceAccountsApi.create(body);
    },
    onSuccess: (row) => {
      onSaved();
      if (!sa) {
        // Re-open in EDIT mode on the freshly-created account so keys can be minted.
        setName(row.name);
      }
      onClose();
    },
  });

  const canSubmit =
    canWrite && name.trim() && (scope === "tray" ? true : appId !== "" && roleId !== "");

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>
            {sa ? t("serviceAccounts.edit", "Modifica service account") : t("serviceAccounts.new", "Nuovo service account")}
          </DialogTitle>
        </DialogHeader>
        <DialogBody className="space-y-4">
          <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
            <Field label={t("common.name", "Nome")}>
              <Input className="h-9" value={name} disabled={!canWrite} onChange={(e) => setName(e.target.value)} />
            </Field>
            <Field
              label={t("serviceAccounts.target", "Target")}
              hint={
                scope === "tray"
                  ? t("serviceAccounts.trayHint", "API della piattaforma (solo admin)")
                  : t("serviceAccounts.appTargetHint", "API di una singola app")
              }
            >
              <select
                className={SELECT}
                value={scope}
                disabled={!canWrite}
                onChange={(e) => setScope(e.target.value as ServiceAccountScope)}
              >
                <option value="app">{t("serviceAccounts.targetApp", "App")}</option>
                <option value="tray">{t("serviceAccounts.targetTray", "Tray (piattaforma)")}</option>
              </select>
            </Field>
          </div>
          {scope === "app" && (
            <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
              <Field
                label={t("serviceAccounts.app", "App")}
                hint={t("serviceAccounts.appHint", "Confina la key a una sola app")}
              >
                <select
                  className={SELECT}
                  value={appId}
                  disabled={!canWrite}
                  onChange={(e) => {
                    setAppId(e.target.value);
                    setRoleId(""); // a role belongs to an app — reset when the app changes
                  }}
                >
                  <option value="">{t("serviceAccounts.pickApp", "— scegli un'app —")}</option>
                  {apps.map((a) => (
                    <option key={a.id} value={a.id}>
                      {a.name}
                    </option>
                  ))}
                </select>
              </Field>
              <Field
                label={t("serviceAccounts.role", "Ruolo")}
                hint={t("serviceAccounts.roleHint", "La key agisce con questo ruolo dell'app (RLS + permessi)")}
              >
                <select
                  className={SELECT}
                  value={roleId}
                  disabled={!canWrite || !appId}
                  onChange={(e) => setRoleId(e.target.value)}
                >
                  <option value="">{t("serviceAccounts.pickRole", "— scegli un ruolo —")}</option>
                  {(rolesQ.data ?? []).map((r) => (
                    <option key={r.id} value={r.id}>
                      {r.name}
                    </option>
                  ))}
                </select>
              </Field>
            </div>
          )}
          <Field label={t("common.description", "Descrizione")}>
            <Input className="h-9" value={description} disabled={!canWrite} onChange={(e) => setDescription(e.target.value)} />
          </Field>

          {/* An app SA has NO Tray permissions — its boundary is the app ROLE (S6). Only a
              TRAY account shows the RBAC matrix. */}
          {scope === "tray" && (
            <div>
              <Label>{t("serviceAccounts.permissions", "Permessi")}</Label>
              <div className="mt-1">
                <PermissionPicker value={permissions} onChange={setPermissions} disabled={!canWrite} />
              </div>
            </div>
          )}

          <label className="flex items-center gap-2 text-sm">
            <Switch checked={enabled} disabled={!canWrite} onCheckedChange={setEnabled} />
            {t("serviceAccounts.enabledLabel", "Abilitato (disabilita = kill-switch di tutte le chiavi)")}
          </label>

          {sa && <KeysSection sa={sa} canWrite={canWrite} justCreated={created} onCreated={setCreated} />}
          {!sa && (
            <Alert tone="info" title={t("serviceAccounts.keysAfterSave", "Le chiavi si generano dopo il salvataggio")}>
              {t("serviceAccounts.keysAfterSaveHint", "Salva il service account, poi riaprilo per generare le API key.")}
            </Alert>
          )}
        </DialogBody>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            {t("common.close", "Chiudi")}
          </Button>
          {canWrite && (
            <Button onClick={() => save.mutate()} disabled={!canSubmit || save.isPending}>
              {t("common.save", "Salva")}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function KeysSection({
  sa,
  canWrite,
  justCreated,
  onCreated,
}: {
  sa: ServiceAccount;
  canWrite: boolean;
  justCreated: ServiceAccountKeyCreated | null;
  onCreated: (k: ServiceAccountKeyCreated | null) => void;
}) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [keyName, setKeyName] = React.useState("");
  const [copied, setCopied] = React.useState(false);
  const keys = useQuery({
    queryKey: ["service-accounts", sa.id, "keys"],
    queryFn: () => serviceAccountsApi.listKeys(sa.id),
  });
  const invalidate = () => qc.invalidateQueries({ queryKey: ["service-accounts", sa.id, "keys"] });

  const mint = useMutation({
    mutationFn: () => serviceAccountsApi.mintKey(sa.id, keyName || undefined),
    onSuccess: (k) => {
      onCreated(k);
      setKeyName("");
      invalidate();
    },
  });
  const revoke = useMutation({
    mutationFn: (keyId: string) => serviceAccountsApi.revokeKey(sa.id, keyId),
    onSuccess: invalidate,
  });

  return (
    <div className="space-y-2 rounded-md border border-border p-2">
      <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
        {t("serviceAccounts.keys", "Chiavi API")}
      </div>
      {justCreated && (
        <Alert tone="success" title={t("serviceAccounts.keyOnce", "Copia ora — non verrà più mostrata")}>
          <div className="mt-1 flex items-center gap-2">
            <code className="flex-1 break-all border border-border bg-muted p-2 font-mono text-xs">
              {justCreated.value}
            </code>
            <Button
              size="sm"
              variant="outline"
              onClick={async () => {
                await navigator.clipboard.writeText(justCreated.value);
                setCopied(true);
                setTimeout(() => setCopied(false), 1500);
              }}
            >
              {copied ? <Check className="h-3.5 w-3.5 text-success" /> : <Copy className="h-3.5 w-3.5" />}
            </Button>
          </div>
        </Alert>
      )}
      {(keys.data ?? []).filter((k) => !k.revoked_at).length === 0 ? (
        <p className="text-[11px] text-muted-foreground">{t("serviceAccounts.noKeys", "Nessuna chiave attiva.")}</p>
      ) : (
        <div className="space-y-1">
          {(keys.data ?? [])
            .filter((k) => !k.revoked_at)
            .map((k) => (
              <div key={k.key_id} className="flex items-center justify-between text-xs">
                <span className="font-mono">{k.name || k.key_id.slice(0, 8)}</span>
                {canWrite && (
                  <Button variant="ghost" size="icon" onClick={() => revoke.mutate(k.key_id)}>
                    <XCircle className="h-3.5 w-3.5 text-danger" strokeWidth={1.5} />
                  </Button>
                )}
              </div>
            ))}
        </div>
      )}
      {canWrite && (
        <div className="flex items-center gap-2">
          <Input
            className="h-8 text-xs"
            placeholder={t("serviceAccounts.keyNamePh", "nome chiave (opzionale)")}
            value={keyName}
            onChange={(e) => setKeyName(e.target.value)}
          />
          <Button size="sm" variant="outline" onClick={() => mint.mutate()} disabled={mint.isPending}>
            <Plus className="mr-1 h-3.5 w-3.5" strokeWidth={2} /> {t("serviceAccounts.mint", "Genera")}
          </Button>
        </div>
      )}
    </div>
  );
}

function Field({ label, hint, children }: { label: string; hint?: string; children: React.ReactNode }) {
  return (
    <label className="flex flex-col gap-1">
      <span className="text-xs font-medium text-muted-foreground">{label}</span>
      {children}
      {hint && <span className="text-[10px] text-muted-foreground/70">{hint}</span>}
    </label>
  );
}
